import { adminApiClient } from './adminApiClient';
import { createNestedCrudService } from './createCrudService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ErpEndpoint {
  id: string;
  erp_registry_id: string;
  slug: string;
  name: string;
  description: string | null;
  http_method: HttpMethod;
  path: string;
  path_parameters: Record<string, unknown> | null;
  query_parameters: Record<string, unknown> | null;
  headers: Record<string, string> | null;
  request_schema: Record<string, unknown> | null;
  request_transform: Record<string, unknown> | null;
  response_schema: Record<string, unknown> | null;
  response_transform: Record<string, unknown> | null;
  example_params: Record<string, unknown> | null;
  timeout_ms: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateErpEndpointPayload {
  erp_registry_id: string;
  slug: string;
  name: string;
  description?: string | null;
  http_method: HttpMethod;
  path: string;
  path_parameters?: Record<string, unknown> | null;
  query_parameters?: Record<string, unknown> | null;
  headers?: Record<string, string> | null;
  request_schema?: Record<string, unknown> | null;
  request_transform?: Record<string, unknown> | null;
  response_schema?: Record<string, unknown> | null;
  response_transform?: Record<string, unknown> | null;
  example_params?: Record<string, unknown> | null;
  timeout_ms?: number | null;
  is_active?: boolean;
}

export interface UpdateErpEndpointPayload {
  slug?: string;
  name?: string;
  description?: string | null;
  http_method?: HttpMethod;
  path?: string;
  path_parameters?: Record<string, unknown> | null;
  query_parameters?: Record<string, unknown> | null;
  headers?: Record<string, string> | null;
  request_schema?: Record<string, unknown> | null;
  request_transform?: Record<string, unknown> | null;
  response_schema?: Record<string, unknown> | null;
  response_transform?: Record<string, unknown> | null;
  example_params?: Record<string, unknown> | null;
  timeout_ms?: number | null;
  is_active?: boolean;
}

export interface DryRunRequest {
  data?: Record<string, unknown>;
  headers?: Record<string, string>;
  dry_run?: boolean;
}

export interface DryRunResponse {
  success: boolean;
  data: unknown;
  error: unknown;
  metadata: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

function basePath(registryId: string): string {
  return `/admin/erp/registries/${registryId}/endpoints`;
}

const crud = createNestedCrudService<
  ErpEndpoint,
  CreateErpEndpointPayload,
  UpdateErpEndpointPayload
>(basePath);

export const erpEndpointsService = {
  ...crud,

  async dryRun(
    registryId: string,
    endpointId: string,
    data?: DryRunRequest,
    tenantId?: string,
    spoofedBearerToken?: string | null,
    spoofedSessionId?: number | null,
  ): Promise<DryRunResponse> {
    const headers: Record<string, string> = {};
    if (tenantId) {
      headers['X-Erp-Tenant-Id'] = tenantId;
    }
    if (spoofedBearerToken) {
      headers['X-Erp-Bearer-Token'] = spoofedBearerToken;
    }
    if (spoofedSessionId != null) {
      headers['X-Erp-Session-Id'] = String(spoofedSessionId);
    }

    const response = await adminApiClient.post<DryRunResponse>(
      `${basePath(registryId)}/${endpointId}/dry-run`,
      data,
      Object.keys(headers).length > 0 ? { headers } : undefined,
    );
    return response.data;
  },
};
